import React, { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faSignOutAlt } from '@fortawesome/free-solid-svg-icons'
import UserRolePill from './UserRolePill'
import { useSafeUserData } from '../stateStores/User'
import Section from './Section'
import SupportEmailLink from './SupportEmailLink'
import { getProfilePicUrl } from '../utils/personUtils'
import { AuthHelper } from '../auth/AuthHelper'
import { UserRoles } from '../api/tenant/models/UserDto'
import DropDown, { DropDownItem } from './DropDown'
import tenantApi from '../api/tenant/TenantApi'

function UserMenu(): React.JSX.Element {
    const { logout } = useAuth0()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { user, tenant } = useSafeUserData()
    // these are only populated for the admin as they have a drop down to select which tenant they are viewing
    const [tenantDropDownItems, setTenantDropDownItems] = useState<
        DropDownItem[]
    >([])
    const [selectTenantId, setSelectTenantId] = useState<string | null>(null)

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        async function fetchTenants() {
            if (AuthHelper.userHasRole(user, UserRoles.ADMIN)) {
                const tenants = (await tenantApi.getAll()).tenants
                const newTenantDropDownItems: DropDownItem[] = []

                for (const tenant of tenants) {
                    newTenantDropDownItems.push({
                        id: tenant.id,
                        value: tenant.name,
                    })
                }

                setTenantDropDownItems(newTenantDropDownItems)

                // now set the selected tenant id
                const tenantOverrideId = AuthHelper.getTenantOverrideId()
                if (tenantOverrideId) {
                    setSelectTenantId(tenantOverrideId)
                } else {
                    // if it hasn't been overridden just use the actual tenant of the user
                    setSelectTenantId(user.tenantId)
                }
            }
        }

        fetchTenants()

        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [user])

    function handleTenantChanged(tenantId: string) {
        // set cookie
        if (tenantId === user.tenantId) {
            // they have selected their real tenant, so we can remove the override
            AuthHelper.removeTenantOverrideId()
        } else {
            AuthHelper.setTenantOverrideId(tenantId)
        }
        setSelectTenantId(tenantId)
        // reload the page
        window.location.reload()
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Profile Image */}
            <img
                src={
                    user.person && getProfilePicUrl(user.person)
                        ? getProfilePicUrl(user.person)
                        : user.auth0PicUrl
                }
                alt="Profile"
                className="rounded-full w-10 h-10 cursor-pointer border border-gray-300"
                onClick={toggleDropdown}
            />

            {isDropdownOpen && (
                <div
                    className="absolute mt-2 w-56 min-w-fit bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                    style={{ right: '10px' }} // Adjusts the dropdown's position to move it slightly left
                >
                    <div className="px-4 pt-3 border-b border-gray-200">
                        <div className="gap-4">
                            <p className="text-lg font-semibold text-gray-900 text-nowrap">
                                {user.person?.firstName || user.person?.lastName
                                    ? `${user.person?.firstName ?? ''} ${
                                          user.person?.lastName ?? ''
                                      }`.trim()
                                    : user.email}
                            </p>

                            {user.person && (
                                <p className="text-sm text-gray-400">
                                    {user.email}
                                </p>
                            )}

                            {AuthHelper.userHasRole(user, UserRoles.ADMIN) &&
                            selectTenantId ? (
                                <div>
                                    <DropDown
                                        id="tenant-dropdown"
                                        name=""
                                        items={tenantDropDownItems}
                                        onItemSelected={handleTenantChanged}
                                        selectedItemId={selectTenantId}
                                    ></DropDown>
                                </div>
                            ) : (
                                <p className="text-md text-gray-600">
                                    {tenant ? `${tenant?.name}` : ''}
                                </p>
                            )}
                        </div>
                        <Section
                            title="Roles"
                            titleType="h2"
                            className="gap-4 mt-2"
                        >
                            <div className="flex flex-wrap gap-1">
                                {user.userRoles.map((userRole) => (
                                    <div key={`user-role-${userRole}`}>
                                        <UserRolePill
                                            value={userRole}
                                        ></UserRolePill>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </div>
                    <SupportEmailLink className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 hover:no-underline">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Support
                    </SupportEmailLink>
                    <button
                        className="flex items-center w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        onClick={() =>
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin,
                                },
                            })
                        }
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                        Log Out
                    </button>
                </div>
            )}
        </div>
    )
}

export default UserMenu
